import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { saffronURL } from "../../Url";


const ConnectBanner = ({
  selectedCountry,
  selectedCategory,
  selectedLanguage,
  selectedState,
  selectedCity,
  hindu_css,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);

  const { data, isPending, isError } = useQuery({
    queryKey: [
      "banner",
      selectedCountry,
      selectedCategory,
      selectedLanguage,
      selectedState,
      selectedCity,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCountry) params.append("country", selectedCountry);
      if (selectedLanguage) params.append("language", selectedLanguage);
      if (selectedCategory) params.append("category", selectedCategory);
      if (selectedState) params.append("state", selectedState);
      if (selectedCity) params.append("city", selectedCity);

      const res = await axios.get(
        `${saffronURL}/api/v1/public/sliders?${params.toString()}`,
      );
      return res.data;
    },
    retry: false,
    staleTime: 1000 * 60 * 60,
    cacheTime: 1000 * 60 * 60,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    keepPreviousData: true,
  });

  // Normalize slides: single object → array
  const slides = data?.data
    ? Array.isArray(data.data)
      ? data.data
      : [data.data]
    : [];

  /* ================= Auto Play ================= */
  useEffect(() => {
    if (!autoPlay || slides?.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides?.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [autoPlay, slides?.length]);

  /* ================= Controls ================= */
  const prevSlide = () => {
    setAutoPlay(false);
    setCurrentSlide((prev) => (prev === 0 ? slides?.length - 1 : prev - 1));
  };

  const nextSlide = () => {
    setAutoPlay(false);
    setCurrentSlide((prev) => (prev + 1) % slides?.length);
  };
  if (isPending) {
    return (
      <section className="relative w-full h-[27vh] md:h-[80vh]  overflow-hidden">
        <div className="absolute inset-0 bg-gray-300" />

        <div
          className="absolute inset-0 
        bg-gradient-to-r 
        from-transparent 
        via-white/40 
        to-transparent 
        animate-[shimmer_1.5s_infinite]"
        />

        <style>
          {`
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
        `}
        </style>
      </section>
    );
  }

  /* ================= Render ================= */
  if (!slides?.length) return null;

  return (
    <section className="relative h-[35vh] md:h-[45vh] lg:h-[80vh] overflow-hidden  ">
      {slides?.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
            index === currentSlide ? "opacity-100 z-10" : "opacity-0 z-0"
          }`}
        >
          {/* IMAGE */}
          <img
            src={slide?.image}
            alt={slide?.title}
            className={`hidden md:block absolute inset-0 w-full h-full  ${hindu_css}`}
          />

          <img
            src={slide?.mob_image || slide?.image}
            alt={slide?.title}
            className="block md:hidden absolute inset-0 w-full h-full object-fill"
          />
        </div>
      ))}

      {/* Arrows */}
      {slides.length > 1 && (
        <>
          {/* PREV */}
          <button
            onClick={prevSlide}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2
    z-[50]
    w-5 h-5 md:w-12 md:h-12
    flex items-center justify-center
    rounded-full
    bg-black/40 hover:bg-black/60
    backdrop-blur
    transition-all duration-300 hover:scale-110"
          >
            <ChevronLeft size={26} strokeWidth={2.2} className="text-white" />
          </button>

          {/* NEXT */}
          <button
            onClick={nextSlide}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2
            z-[50]
            w-5 h-5 md:w-12 md:h-12
            flex items-center justify-center
            rounded-full
            bg-black/40 hover:bg-black/60
            backdrop-blur
            transition-all duration-300 hover:scale-110"
          >
            <ChevronRight size={26} strokeWidth={2.2} className="text-white " />
          </button>

          <div className="absolute bottom-0 md:bottom-1 left-1/2 -translate-x-1/2 z-[50]">
            <div
              className="flex items-center gap-3 px-1 md:px-4 py-0 md:py-2 rounded-full
        bg-black/40 backdrop-blur-md"
            >
              {slides.map((_, i) => (
                <span
                  key={i}
                  onClick={() => {
                    setAutoPlay(false);
                    setCurrentSlide(i);
                  }}
                  className={`h-2.5 rounded-full cursor-pointer transition-all duration-300 ${
                    i === currentSlide
                      ? "w-8 bg-orange-500"
                      : "w-2.5 bg-white/60 hover:bg-white"
                  }`}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default ConnectBanner;
